import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Restaurant } from '@cfa/brand-icons';
import { Button, Card, CardContent, Typography } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { useRouteMatcher } from '@/utils/url';

import Constants, { routes } from '@/constants';

import { LocationDetails } from '@/_generated/api-clients/xp';
import {
  saveUserPreferences,
  SaveUserPreferencesData,
} from '@/_generated/api-clients/xp';
import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface LocationCardProps {
  location: LocationDetails;
}

export const LocationCard: React.FC<LocationCardProps> = ({ location }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routeMatches = useRouteMatcher();

  const { data: auth } = useAuth();
  const userId = auth?.userId;
  const userPrefs = useAtomValue(userPrefsAtom);
  const [{ selected: selectedLicenseeLocation }, sendLicenseeLocationAction] =
    useAtom(licenseeLocationAtom);

  const userPreferences = (preferences: SaveUserPreferencesData['body']) => {
    saveUserPreferencesXpApi({ preferences });
  };

  const saveUserPreferencesXpApi = ({
    preferences,
  }: {
    preferences: SaveUserPreferencesData['body'];
  }) => {
    saveUserPreferences({ body: preferences }).catch((err: unknown) =>
      datadogRum.addError(err),
    );
  };

  const handleRedirects = () => {
    // redirecting users when changing locations on pages the new location might not have access to
    switch (true) {
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingAssignedPath,
      ):
        navigate({
          pathname: routes.plans.plans,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingPlanEditPath,
      ):
        navigate({
          pathname: routes.plans.managedPlans,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingTeamMembersPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.TrainingTeamMemberProgressPath,
        ):
        navigate({
          pathname: routes.plans.managedPlans,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingBuildQuizzesPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.TrainingTeamMemberProgressPath,
        ):
        navigate({
          pathname: routes.plans.managedQuizzes,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingEditQuizzesPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.TrainingTeamMemberProgressPath,
        ):
        navigate({
          pathname: routes.plans.managedQuizzes,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.ReportsPlansViewPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.ReportsTeamMemberProgressViewPath,
        ):
        navigate({
          pathname: routes.reports.plans,
        });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersViewPath,
      ):
        navigate({
          pathname: routes.reports.teamMembers,
        });
        break;
    }
  };

  const handleChoose = () => {
    sendLicenseeLocationAction('hide');

    if (selectedLicenseeLocation?.number !== location.number) {
      localStorage.setItem('licenseeLocation', JSON.stringify(location));
      sendLicenseeLocationAction('setActive', location);
      sendLicenseeLocationAction('setActive', location);
      userPreferences({
        country: userPrefs.country.id ?? '',
        language: userPrefs.language,
        location: location.number ?? '',
        userId,
      });
      handleRedirects();
    }
  };

  return (
    <Card
      data-testid="LocationCard"
      elevation={1}
      className="relative my-4 flex h-auto animate-[fadeIn_linear_0.3s] flex-row! overflow-visible p-0!"
    >
      <div
        className="flex h-auto w-20 shrink-0 items-center justify-center rounded-l-[inherit]"
        style={{ backgroundColor: Constants.PLAN_CATEGORY_COLORS.GENERAL }}
      >
        <Restaurant style={{ color: 'white' }} size="md" />
      </div>
      <CardContent className="flex w-full items-center justify-between gap-2 p-4! text-left">
        <div className="flex min-w-0 grow flex-col items-start justify-start overflow-hidden md:justify-center">
          <Typography
            data-testid="LocationName"
            fontWeight="bold"
            variant="body1"
            className="line-clamp-2 max-w-full min-w-0 overflow-hidden text-ellipsis"
          >
            {location.number}
          </Typography>
          <Typography data-testid="LocationName" variant="body2">
            {location.name}
          </Typography>
        </div>
        <div className="flex shrink-0 items-center justify-center gap-4">
          <Button
            className="min-w-[58px]! md:min-w-[200px]"
            color="secondary"
            data-testid="templateCopyButtonTest"
            onClick={handleChoose}
            variant="filled"
          >
            {t('Button.choose')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
